/**
 * GroupHealthIndicator
 *
 * Visual dashboard showing group vitality: green / orange / red.
 * Metrics: attendance, reflection completion, commitment fulfillment, consistency.
 *
 * Uses memoized computation — reads from useStructuredGrowth which caches results.
 * Exposes a "Ververs" button for manual recalculation.
 */

import React, { memo, useMemo } from 'react';
import {
  GrowthIcon,
  SuccessIcon,
  WarningIcon,
  ErrorIcon,
  GoalIcon,
  InsightIcon,
  CalendarIcon,
  ClockIcon,
  RefreshCw,
  Loader,
} from '../../components/icons';
import { useStructuredGrowth } from './useStructuredGrowth';
import type { StructuredGrowthEntityType, HealthStatus, GroupHealthStats } from './types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface GroupHealthIndicatorProps {
  entityId: string;
  entityType: StructuredGrowthEntityType;
  /** Compact mode: single pill badge (for embedding in headers) */
  compact?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_CONFIG: Record<
  HealthStatus,
  { label: string; bg: string; border: string; text: string; dot: string }
> = {
  green: {
    label: 'Gezond',
    bg: 'bg-emerald-50',
    border: 'border-emerald-100',
    text: 'text-emerald-700',
    dot: 'bg-emerald-500',
  },
  orange: {
    label: 'Aandacht vereist',
    bg: 'bg-amber-50',
    border: 'border-amber-100',
    text: 'text-amber-700',
    dot: 'bg-amber-400',
  },
  red: {
    label: 'Kritiek',
    bg: 'bg-red-50',
    border: 'border-red-100',
    text: 'text-red-700',
    dot: 'bg-red-500',
  },
};

interface MetricConfig {
  label: string;
  value: number;
  icon: React.ReactNode;
  threshold: { ok: number; warn: number };
}

function metricColor(value: number, threshold: { ok: number; warn: number }): string {
  if (value >= threshold.ok) return 'bg-emerald-500';
  if (value >= threshold.warn) return 'bg-amber-400';
  return 'bg-red-400';
}

function metricTextColor(value: number, threshold: { ok: number; warn: number }): string {
  if (value >= threshold.ok) return 'text-emerald-600';
  if (value >= threshold.warn) return 'text-amber-500';
  return 'text-red-500';
}

// ---------------------------------------------------------------------------
// Metric bar row
// ---------------------------------------------------------------------------

const MetricRow = memo(function MetricRow({ metric }: { metric: MetricConfig }) {
  const barColor = metricColor(metric.value, metric.threshold);
  const valueColor = metricTextColor(metric.value, metric.threshold);

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {metric.icon}
          <span className="text-xs text-gray-600">{metric.label}</span>
        </div>
        <span className={`text-xs font-semibold ${valueColor}`}>{metric.value}%</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${barColor}`}
          style={{ width: `${metric.value}%` }}
        />
      </div>
    </div>
  );
});

// ---------------------------------------------------------------------------
// Status icon
// ---------------------------------------------------------------------------

function StatusIcon({ status }: { status: HealthStatus }) {
  if (status === 'green')
    return <SuccessIcon size={16} weight="fill" className="text-emerald-500" />;
  if (status === 'orange')
    return <WarningIcon size={16} weight="fill" className="text-amber-400" />;
  return <ErrorIcon size={16} weight="fill" className="text-red-500" />;
}

// ---------------------------------------------------------------------------
// Compact badge
// ---------------------------------------------------------------------------

const CompactBadge = memo(function CompactBadge({
  health,
}: {
  health: GroupHealthStats | null;
}) {
  if (!health) return null;
  const cfg = STATUS_CONFIG[health.status];
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${cfg.bg} ${cfg.border} ${cfg.text}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
});

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const GroupHealthIndicator: React.FC<GroupHealthIndicatorProps> = ({
  entityId,
  entityType,
  compact = false,
}) => {
  const { health, loading, refreshHealth } = useStructuredGrowth(entityId, entityType);

  const metrics = useMemo((): MetricConfig[] => {
    if (!health) return [];
    return [
      {
        label: 'Aanwezigheid',
        value: health.attendancePercent,
        icon: <CalendarIcon size={12} weight="regular" className="text-gray-400" />,
        threshold: { ok: 70, warn: 40 },
      },
      {
        label: 'Reflectie voltooiing',
        value: health.reflectionCompletionPercent,
        icon: <InsightIcon size={12} weight="regular" className="text-gray-400" />,
        threshold: { ok: 75, warn: 40 },
      },
      {
        label: 'Commitment nakoming',
        value: health.commitmentFulfillmentPercent,
        icon: <GoalIcon size={12} weight="regular" className="text-gray-400" />,
        threshold: { ok: 70, warn: 40 },
      },
      {
        label: 'Activiteitsconsistentie',
        value: health.activityConsistency,
        icon: <ClockIcon size={12} weight="regular" className="text-gray-400" />,
        threshold: { ok: 75, warn: 50 },
      },
    ];
  }, [health]);

  if (compact) {
    return loading ? (
      <Loader size={14} strokeWidth={1.5} className="animate-spin text-gray-300" />
    ) : (
      <CompactBadge health={health} />
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <GrowthIcon size={16} weight="regular" className="text-gray-500" />
          <span className="text-sm font-semibold text-gray-900">Groepsgezondheid</span>
        </div>
        <button
          onClick={refreshHealth}
          disabled={loading}
          className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-40"
          title="Ververs"
        >
          <RefreshCw
            size={13}
            strokeWidth={2}
            className={loading ? 'animate-spin' : ''}
          />
        </button>
      </div>
      <p className="text-xs text-gray-400 mb-4">
        Een automatische score op basis van aanwezigheid, ingevulde reflecties en nagekomen afspraken. Groen = de groep is actief en consistent. Oranje of rood = er is aandacht nodig.
      </p>

      {loading && !health ? (
        <div className="flex items-center justify-center py-8">
          <Loader size={18} strokeWidth={1.5} className="animate-spin text-gray-300" />
        </div>
      ) : !health ? (
        <p className="text-xs text-gray-400 text-center py-6">
          Nog niet genoeg data om de groepsgezondheid te berekenen.
        </p>
      ) : (
        <>
          {/* Status badge */}
          {(() => {
            const cfg = STATUS_CONFIG[health.status];
            return (
              <div
                className={`flex items-center gap-2 rounded-xl border px-4 py-3 mb-5 ${cfg.bg} ${cfg.border}`}
              >
                <StatusIcon status={health.status} />
                <div>
                  <p className={`text-sm font-semibold ${cfg.text}`}>{cfg.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Bijgewerkt{' '}
                    {health.calculatedAt.toLocaleTimeString('nl-NL', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
              </div>
            );
          })()}

          {/* Metric bars */}
          <div className="space-y-4">
            {metrics.map((m) => (
              <MetricRow key={m.label} metric={m} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default memo(GroupHealthIndicator);
